import {
  SUPERNOVA_CHAIN_ID,
  SUPERNOVA_COLLECTION_ADDRESS,
  SUPERNOVA_STAKING_ADDRESS,
} from "./supernovaContracts";
import { SECONDS_PER_DAY } from "./supernovaWriteArgs";

/**
 * supernovaStakeRead — the read descriptor that turns the live staking
 * contract's storage into the stake lots the Supernova tab renders.
 *
 * This is the module supernovaContracts.SUPERNOVA_STAKING_READ was a null
 * placeholder for. It lives on its own because it needs the seconds→days
 * conversion from supernovaWriteArgs, and supernovaWriteArgs already imports
 * supernovaContracts — putting it there would close a cycle.
 *
 * PURE AND DEPENDENCY-FREE, exactly like supernovaWriteArgs: no viem, no React,
 * no network. supernovaChain does the talking; everything here is the mapping
 * and the filtering, so all of it is testable without a chain.
 *
 * THE READ IS TWO HOPS, both verified in SUPERNOVA_ABI_FACTS.md:
 *
 *   1. getUserStakes(address) → uint256[]     every stake id this wallet has
 *                                             EVER opened — closed ones included
 *   2. stakes(uint256) → 8 fields             the lot behind one id
 *
 * There is no batch accessor and no "open stakes only" accessor on this
 * contract, so hop 2 runs once per id. supernovaChain paces those calls.
 *
 * WHAT GETS FILTERED OUT, AND WHY EACH IS A FILTER RATHER THAN AN ERROR:
 *
 *   status ≠ 0   a closed lot. The enum is 0 at stake time and 1 after BOTH
 *                exits (facts doc, open item 1), so 0 is the only value that
 *                means open. 297 stakes ever, 28 open — most of what hop 1
 *                returns for a long-standing member is history.
 *   token ≠ the Supernova collection
 *                this contract stakes four whitelisted ERC-1155 collections.
 *                A member's stake in another one is real, it is simply not this
 *                tab's business, and dropping it is not hiding anything from
 *                them here.
 *
 * EVERYTHING ELSE THROWS. A lot we can see but cannot read is never silently
 * skipped and never coerced to a zero: skipping it would quietly shrink the
 * member's staked count (and with it their slot count), which is the same
 * zeros-as-data failure the whole data layer exists to prevent.
 */

/* BigInt is ES2020 and every browser in our browserslist has it; the CRA eslint
   env doesn't declare it (same note as supernovaChain.js). */
/* global BigInt */

/** Hop 1 — verified `C` against the live contract (selector 0x842e2981). */
export const SUPERNOVA_STAKING_IDS_ABI = [
  "function getUserStakes(address user) view returns (uint256[])",
];

/**
 * Hop 2 — verified `C` (selector 0xd5a44f86). The field ORDER is the part that
 * matters and is copied from the facts doc verbatim; a reordering here decodes
 * cleanly and silently means something else.
 */
export const SUPERNOVA_STAKING_LOT_ABI = [
  "function stakes(uint256 stakeId) view returns (address user, address token, " +
    "uint256 tokenId, uint256 amount, uint256 duration, uint256 startTime, " +
    "uint256 unlockTime, uint8 status)",
];

/**
 * StakeStatus. Only 0 and 1 have ever been observed: 0 at stake time, 1 after
 * either exit. So `status === 0` is the one thing we can say means "still open",
 * and anything else is treated as closed rather than guessed at.
 */
export const STAKE_STATUS_OPEN = 0;

/** The eight `stakes()` fields, in the verified order. */
const LOT_FIELDS = [
  "user",
  "token",
  "tokenId",
  "amount",
  "duration",
  "startTime",
  "unlockTime",
  "status",
];

const sameAddress = (a, b) =>
  typeof a === "string" &&
  typeof b === "string" &&
  a.toLowerCase() === b.toLowerCase();

/** bigint | number | decimal string → number. null when it isn't one of those. */
function toNumber(value) {
  if (typeof value === "bigint") {
    return value >= 0 && value <= BigInt(Number.MAX_SAFE_INTEGER)
      ? Number(value)
      : null;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && /^\d+$/.test(value)) {
    const n = Number(value);
    return Number.isSafeInteger(n) ? n : null;
  }
  return null;
}

/**
 * Days for a contract `duration` in seconds.
 *
 * DELIBERATELY MORE PERMISSIVE THAN THE WRITE SIDE. supernovaWriteArgs accepts
 * only the six presets, because those are the only locks we offer. This is the
 * read side: it has to render a lock somebody ALREADY made, and the live
 * contract's history holds durations we would never offer (86,400 = one day is
 * the contract's own minimum). A fractional day is fine — the XP model rates by
 * whole days completed and handles the remainder pro-rata.
 *
 * @returns {number|null} days, or null when the duration isn't readable
 */
export function lockDaysFromSeconds(duration) {
  const seconds = toNumber(duration);
  if (seconds === null || seconds <= 0) return null;
  return seconds / SECONDS_PER_DAY;
}

/** Contract time (seconds) → epoch ms. */
function msFromSeconds(value) {
  const seconds = toNumber(value);
  if (seconds === null || seconds <= 0) return null;
  return seconds * 1000;
}

/**
 * The stake ids from hop 1, as bigints hop 2 can be called with.
 *
 * Throws on anything that isn't a list of ids: an unreadable id list is not an
 * empty one, and returning [] here would render as "you have no stakes".
 *
 * @param {unknown} raw the `uint256[]` getUserStakes returned
 * @returns {bigint[]}
 */
export function toStakeIds(raw) {
  if (!Array.isArray(raw)) {
    throw new Error("getUserStakes did not return a list of stake ids");
  }
  return raw.map((id) => {
    if (typeof id === "bigint") {
      if (id < 0) throw new Error(`negative stake id ${String(id)}`);
      return id;
    }
    const n = toNumber(id);
    if (n === null || !Number.isInteger(n) || n < 0) {
      throw new Error(`unreadable stake id ${String(id)}`);
    }
    return BigInt(n);
  });
}

/**
 * One `stakes()` response as named fields. viem hands back an array for a
 * multi-output function; a client that returns the named object works too.
 */
function lotFields(record) {
  if (Array.isArray(record)) {
    if (record.length !== LOT_FIELDS.length) return null;
    return LOT_FIELDS.reduce((out, name, i) => {
      out[name] = record[i];
      return out;
    }, {});
  }
  if (record && typeof record === "object") {
    return LOT_FIELDS.every((name) => name in record) ? record : null;
  }
  return null;
}

/**
 * The lot id every downstream surface keys on, and — critically — the value the
 * exit writes are signed against. supernovaWriteArgs.stakeIdArg takes the last
 * `:` segment, so the contract's own stakeId has to be exactly that.
 */
export const lotIdFor = (stakeId) =>
  `${SUPERNOVA_CHAIN_ID}:${SUPERNOVA_STAKING_ADDRESS.toLowerCase()}:${String(stakeId)}`;

/**
 * Decode one `stakes()` record into the lot shape supernovaChain normalizes.
 *
 * @param {unknown} record the raw hop-2 response
 * @param {{stakeId: bigint|number|string, wallet: string}} context
 * @returns {Object|null} the lot, or null when this stake is not one this tab
 *          shows (closed, or in another collection)
 * @throws {Error} when the record is ours and open but unreadable
 */
export function decodeStakeRecord(record, context = {}) {
  const { stakeId, wallet } = context;
  const fields = lotFields(record);
  if (!fields) {
    throw new Error(`stake ${String(stakeId)} came back in an unexpected shape`);
  }

  /* Filters first, so a closed lot or another collection's lot never reaches
     the validation below and can never fail the whole read. */
  const status = toNumber(fields.status);
  if (status !== STAKE_STATUS_OPEN) return null;
  if (!sameAddress(fields.token, SUPERNOVA_COLLECTION_ADDRESS)) return null;

  /* getUserStakes(wallet) returning somebody else's stake would mean our field
     mapping has slipped. That is not a lot to skip — it is a decode we cannot
     trust at all, and every other field in the record is suspect with it. */
  if (wallet && !sameAddress(fields.user, wallet)) {
    throw new Error(
      `stake ${String(stakeId)} belongs to another wallet — field mapping is wrong`
    );
  }

  const lockDays = lockDaysFromSeconds(fields.duration);
  const stakedAtMs = msFromSeconds(fields.startTime);
  const unlockAtMs = msFromSeconds(fields.unlockTime);
  const qty = toNumber(fields.amount);
  const tokenId = toNumber(fields.tokenId);

  if (lockDays === null || stakedAtMs === null || qty === null || tokenId === null) {
    throw new Error(`stake ${String(stakeId)} has fields we couldn't read`);
  }

  return {
    lotId: lotIdFor(stakeId),
    tokenId,
    qty,
    lockDays,
    stakedAtMs,
    /* The contract's own unlockTime, not a derived one: it is the figure the
       exit is actually judged against. Verified as startTime + duration on a
       real stake, so deriving it would agree — but only the stored one is the
       contract's answer. Absent ⇒ let the XP model derive it. */
    ...(unlockAtMs === null ? {} : { unlockAtMs }),
  };
}

/**
 * Hop 2's responses → the member's open Supernova lots.
 *
 * @param {Array<{stakeId: bigint, record: unknown}>} entries
 * @param {string} wallet checksummed address the read was made for
 * @returns {Array<Object>}
 */
export function decodeLots(entries, wallet) {
  if (!Array.isArray(entries)) {
    throw new Error("the stake records came back in an unexpected shape");
  }
  return entries
    .map((entry) => decodeStakeRecord(entry && entry.record, { ...entry, wallet }))
    .filter(Boolean);
}

/**
 * The descriptor supernovaChain.getStaked drives. Supplying it is what turns
 * getStaked from "unavailable" into a real read.
 */
export const SUPERNOVA_STAKING_READ = {
  abi: SUPERNOVA_STAKING_IDS_ABI,
  functionName: "getUserStakes",
  buildArgs: (wallet) => [wallet],
  /* Presence of `lot` is what tells getStaked this read is two hops. */
  lot: {
    address: SUPERNOVA_STAKING_ADDRESS,
    abi: SUPERNOVA_STAKING_LOT_ABI,
    functionName: "stakes",
    buildArgs: (stakeId) => [stakeId],
  },
  toStakeIds,
  decodeLots,
};

export default SUPERNOVA_STAKING_READ;
